import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, FormControlLabel, Switch, TextField, Typography } from '@mui/material';
import SaveOutlined from '@mui/icons-material/SaveOutlined';
import SmartToyOutlined from '@mui/icons-material/SmartToyOutlined';
import { Title, useNotify, useTranslate } from 'react-admin';
import { companyAiConfigService, type CompanyAiConfig, type CompanyAiConfigInput } from '../../services/companyAiConfigService';
import tokenService from '../../services/tokenService';
import '../AiGenerator/ai-generator.css';

const SWITCHES = ['contentGeneratorEnabled', 'qualityCheckEnabled', 'knowledgeBaseEnabled', 'helpBotEnabled', 'recommendationsEnabled', 'learningPathsEnabled', 'requireSignOffForAiCourses'] as const;
const CAPS = ['dailyCourseGenerationLimit', 'dailyContentJobLimit', 'dailyVariantLimit'] as const;

/**
 * KI-Einstellungen des Unternehmens (Tranche B4 follow-up, #489): feature
 * switches, sign-off requirement, tutor prompt; daily caps editable by the
 * operator only, who may also pick another company by id.
 */
export const AiSettings: React.FC = () => {
    const translate = useTranslate();
    const notify = useNotify();
    const isPlatform = tokenService.getRoles().some((r) => r === 'ROLE_OPERATOR' || r === 'ROLE_PLATFORM_ADMIN');
    const [companyId, setCompanyId] = useState<string>(isPlatform ? '' : String(tokenService.getCompanyId() ?? ''));
    const [config, setConfig] = useState<CompanyAiConfig | null>(null);
    const [draft, setDraft] = useState<CompanyAiConfigInput>({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        if (isPlatform && !companyId.trim()) { setConfig(null); return; }
        setLoading(true);
        setError(null);
        try {
            const data = await companyAiConfigService.get(isPlatform ? Number(companyId) : undefined);
            setConfig(data);
            setDraft({});
        } catch {
            setConfig(null);
            setError(translate('aiSettings.loadError'));
        } finally {
            setLoading(false);
        }
    }, [companyId, isPlatform, translate]);

    useEffect(() => { void load(); }, [load]);

    const value = <K extends keyof CompanyAiConfigInput>(key: K): CompanyAiConfig[K] => (draft[key] !== undefined ? (draft[key] as CompanyAiConfig[K]) : (config as CompanyAiConfig)[key]);

    const save = async () => {
        if (!config) return;
        setSaving(true);
        try {
            setConfig(await companyAiConfigService.update(draft, isPlatform ? config.companyId : undefined));
            setDraft({});
            notify(translate('aiSettings.saved'), { type: 'success' });
        } catch (e) {
            const data = (e as { response?: { data?: { message?: string; detail?: string } } })?.response?.data;
            notify(data?.message ?? data?.detail ?? translate('aiSettings.saveError'), { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="oa-ai-generator">
            <Title title={translate('aiSettings.title')} />
            <div className="oa-aig-header">
                <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>{translate('aiSettings.title')}</Typography>
                <Chip size="small" icon={<SmartToyOutlined />} label={translate('aiGenerator.badge')} />
            </div>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{translate('aiSettings.intro')}</Typography>

            {isPlatform && (
                <div className="oa-aig-card">
                    <h2 className="oa-aig-card-title">{translate('aiSettings.company')}</h2>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{translate('aiSettings.companyHint')}</Typography>
                    <TextField size="small" type="number" label={translate('aiSettings.companyId')} value={companyId} onChange={(e) => setCompanyId(e.target.value)} inputProps={{ 'aria-label': translate('aiSettings.companyId') }} />
                </div>
            )}

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {loading && <CircularProgress size={24} />}

            {config && !loading && (
                <>
                    <div className="oa-aig-card">
                        <h2 className="oa-aig-card-title">{translate('aiSettings.features')}</h2>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            {SWITCHES.map((key) => (
                                <FormControlLabel
                                    key={key}
                                    control={<Switch checked={value(key)} onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.checked }))} inputProps={{ 'aria-label': translate(`aiSettings.switch.${key}`) }} />}
                                    label={<span><strong>{translate(`aiSettings.switch.${key}`)}</strong><br /><Typography variant="caption" color="text.secondary">{translate(`aiSettings.switchHint.${key}`)}</Typography></span>}
                                />
                            ))}
                        </Box>
                    </div>

                    <div className="oa-aig-card">
                        <h2 className="oa-aig-card-title">{translate('aiSettings.tutorPrompt')}</h2>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{translate('aiSettings.tutorPromptHint')}</Typography>
                        <TextField fullWidth multiline minRows={3} size="small" value={value('tutorPrompt') ?? ''} onChange={(e) => setDraft((d) => ({ ...d, tutorPrompt: e.target.value }))} inputProps={{ maxLength: 4000, 'aria-label': translate('aiSettings.tutorPrompt') }} />
                    </div>

                    <div className="oa-aig-card">
                        <h2 className="oa-aig-card-title">{translate('aiSettings.limits')}</h2>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{translate(config.canEditLimits ? 'aiSettings.limitsHintOperator' : 'aiSettings.limitsHint')}</Typography>
                        <div className="oa-aig-form-row">
                            {CAPS.map((key) => (
                                <TextField
                                    key={key}
                                    size="small"
                                    type="number"
                                    label={translate(`aiSettings.cap.${key}`)}
                                    value={value(key)}
                                    disabled={!config.canEditLimits}
                                    helperText={translate('aiSettings.capDefault', { value: config.defaults[key] })}
                                    onChange={(e) => setDraft((d) => ({ ...d, [key]: Math.max(0, Number(e.target.value)) }))}
                                    inputProps={{ min: 0, max: 10000, 'aria-label': translate(`aiSettings.cap.${key}`) }}
                                />
                            ))}
                        </div>
                    </div>

                    <div className="oa-aig-actions">
                        <Button variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveOutlined />} onClick={() => void save()} disabled={saving || Object.keys(draft).length === 0}>
                            {translate('ra.action.save')}
                        </Button>
                    </div>
                </>
            )}
        </div>
    );
};

export default AiSettings;
